import { useEffect } from 'react';
import { create } from 'zustand';

/**
 * Which body the app sidebar shows (ENG-1161): the normal navigation
 * ("Browse") or Claire's chat history ("Chat").
 *
 * How the mode is remembered:
 *  - Each AREA has a default: the Claire product (`/assistant`) opens in Chat,
 *    everything else in Browse.
 *  - Clicking the toggle records a choice FOR THE CURRENT AREA. It survives
 *    navigation inside that area, and the layout swap between the dashboard
 *    shell and the assistant shell (each mounts its own `AppSidebar`, so this
 *    lives in a module store rather than component state).
 *  - Crossing into the other area drops the choice and falls back to that
 *    area's default: picking a chat from the history lands you on
 *    `/assistant` still in Chat, and picking a nav item from Browse lands you
 *    in the dashboard in Browse.
 *
 * Deliberately NOT persisted across reloads — the route default is the right
 * answer on a fresh load.
 */
export type SidebarMode = 'browse' | 'chat';
export type SidebarArea = 'assistant' | 'app';

export interface SidebarModeChoice {
  mode: SidebarMode;
  area: SidebarArea;
}

export function sidebarAreaFor(pathname: string): SidebarArea {
  return pathname === '/assistant' || pathname.startsWith('/assistant/')
    ? 'assistant'
    : 'app';
}

export function defaultSidebarMode(area: SidebarArea): SidebarMode {
  return area === 'assistant' ? 'chat' : 'browse';
}

/** The mode to show: the user's choice if it was made in this area, else the area default. */
export function resolveSidebarMode(
  choice: SidebarModeChoice | null,
  area: SidebarArea
): SidebarMode {
  return choice && choice.area === area
    ? choice.mode
    : defaultSidebarMode(area);
}

interface SidebarModeStore {
  choice: SidebarModeChoice | null;
  setChoice: (choice: SidebarModeChoice | null) => void;
}

export const useSidebarModeStore = create<SidebarModeStore>((set) => ({
  choice: null,
  setChoice: (choice) => set({ choice }),
}));

/** Current sidebar mode for `pathname`, plus a setter that records the choice for this area. */
export function useSidebarMode(pathname: string) {
  const area = sidebarAreaFor(pathname);
  const choice = useSidebarModeStore((s) => s.choice);
  const setChoice = useSidebarModeStore((s) => s.setChoice);

  // Forget a choice made in the other area once we've left it, so coming back
  // later starts from that area's default rather than a stale click.
  useEffect(() => {
    if (choice && choice.area !== area) setChoice(null);
  }, [area, choice, setChoice]);

  return {
    mode: resolveSidebarMode(choice, area),
    setMode: (mode: SidebarMode) => setChoice({ mode, area }),
  };
}
